import type { EmployeeRole } from '../types/auth';
import type { UiMerchantStatus } from '../api/types';

export const roleLabels: Record<EmployeeRole, string> = {
  ceo: 'Chief Executive Officer',
  cto: 'Chief Technology Officer',
  compliance_head: 'Compliance Head',
  compliance_officer: 'Compliance Officer',
  finance_manager: 'Finance Manager',
  accountant: 'Accountant',
  support_lead: 'Support Lead',
  support_operator: 'Support Operator',
};

export function isCeo(role: EmployeeRole | null): boolean {
  return role === 'ceo';
}

export function isReadOnlyRole(role: EmployeeRole | null): boolean {
  return role === 'accountant';
}

export function canAccessMerchants(role: EmployeeRole | null): boolean {
  return (
    role !== null &&
    [
      'ceo',
      'cto',
      'compliance_head',
      'compliance_officer',
      'finance_manager',
      'accountant',
      'support_lead',
    ].includes(role)
  );
}

export function canAccessKyb(role: EmployeeRole | null): boolean {
  return role === 'ceo' || role === 'compliance_head' || role === 'compliance_officer';
}

export function canAccessTransactions(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (
    [
      'ceo',
      'cto',
      'compliance_head',
      'compliance_officer',
      'finance_manager',
      'accountant',
      'support_lead',
      'support_operator',
    ] as EmployeeRole[]
  ).includes(role);
}

export function canAccessStaff(role: EmployeeRole | null): boolean {
  return role === 'ceo';
}

export function canEditMerchantFees(role: EmployeeRole | null): boolean {
  return role === 'ceo';
}

export function canChangeMerchantStatus(role: EmployeeRole | null): boolean {
  return role === 'ceo' || role === 'compliance_head' || role === 'compliance_officer';
}

export function canBlacklistMerchant(role: EmployeeRole | null): boolean {
  return role === 'ceo' || role === 'compliance_head';
}

export function canEditMerchantProfileBlocks(role: EmployeeRole | null): boolean {
  return role === 'ceo';
}

/** On-chain settlement wallet — aligned with BFF RBAC.merchants.editProfile */
export function canEditSettlementWallet(role: EmployeeRole | null): boolean {
  return role === 'ceo' || role === 'compliance_head';
}

export function canViewWalletChangelog(role: EmployeeRole | null): boolean {
  return canAccessKyb(role);
}

export function canExportTransactions(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (
    [
      'ceo',
      'cto',
      'compliance_head',
      'compliance_officer',
      'finance_manager',
      'accountant',
    ] as EmployeeRole[]
  ).includes(role);
}

export function canAccessAuditLogs(role: EmployeeRole | null): boolean {
  return canAccessKyb(role);
}

export function canAccessCargo(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (
    [
      'ceo',
      'cto',
      'compliance_head',
      'finance_manager',
      'support_lead',
      'support_operator',
    ] as EmployeeRole[]
  ).includes(role);
}

export function canManageCargo(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (['ceo', 'compliance_head', 'finance_manager'] as EmployeeRole[]).includes(role);
}

export function canSupportCargo(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (
    ['ceo', 'compliance_head', 'support_lead', 'support_operator'] as EmployeeRole[]
  ).includes(role);
}

export function canEditCargoCommission(role: EmployeeRole | null): boolean {
  return isCeo(role);
}

export function canAccessSystem(role: EmployeeRole | null): boolean {
  if (!role) return false;
  return (['ceo', 'cto', 'compliance_head', 'finance_manager'] as EmployeeRole[]).includes(role);
}

export function canExecutePayout(role: EmployeeRole | null): boolean {
  return role === 'ceo';
}

export function canCreatePayoutRequest(role: EmployeeRole | null): boolean {
  return role === 'ceo' || role === 'finance_manager';
}

export function allowedQuickStatuses(role: EmployeeRole | null): UiMerchantStatus[] {
  const all: UiMerchantStatus[] = ['active', 'pending', 'suspended', 'blacklisted'];
  if (!role || role === 'ceo' || role === 'compliance_head') return all;
  if (role === 'compliance_officer') return ['active', 'pending', 'suspended'];
  return [];
}

export function shouldMaskPii(role: EmployeeRole | null): boolean {
  return role === 'support_operator';
}
